/**
 * A projectile look (ADR-095 decision 19): the strip a shot shows in flight, the strips its burst plays one after
 * another at the landing point, whether the sprite turns to its heading, and how far it is lifted at mid-flight. An
 * ability's projectile shape names a look by its id (`shape.look`); the page reads the looks it was served from a table
 * and draws nothing for an id the table lacks. A strip is one row of frames, left to right. The table is read from JSON
 * a server hands the page, so every field is held to its kind and a range, and a path is held under the asset base.
 */

/**
 * One strip of frames: where its image is under the asset base, a frame's size in pixels, how many frames the row
 * holds and how long each shows.
 */
export interface ProjectileStrip {
  readonly path: string;
  readonly frameWidth: number;
  readonly frameHeight: number;
  readonly frames: number;
  readonly frameMs: number;
}

/**
 * One strip of a burst, played `loops` times through before the next strip starts.
 */
export interface ProjectileBurstStrip extends ProjectileStrip {
  readonly loops: number;
}

/**
 * A look. `burst` may be empty, for a shot that ends at its landing. `rotates` turns the flight's sprite to the line
 * it flies; `arcPixels` lifts it by that many pixels at mid-flight over a shadow on the line, 0 for a level flight;
 * `launchPixels` is how far above the shooter's feet point the sprite starts, the hand or the mouth it leaves, from
 * which it comes down evenly to the ground at the landing, 0 when the look was written without it.
 */
export interface ProjectileLook {
  readonly id: string;
  readonly flight: ProjectileStrip;
  readonly burst: readonly ProjectileBurstStrip[];
  readonly rotates: boolean;
  readonly arcPixels: number;
  readonly launchPixels: number;
}

/**
 * The looks a page was served, by id.
 */
export interface ProjectileLookTable {
  readonly looks: Readonly<Record<string, ProjectileLook>>;
}

/**
 * Which strip of a burst shows, and which of its frames.
 */
export interface BurstFrame {
  strip: number;
  frame: number;
}

/**
 * The most strips a burst plays.
 */
export const MAX_BURST_STRIPS = 4;

/**
 * The most looks a table holds.
 */
export const MAX_PROJECTILE_LOOKS = 256;

const MAX_FRAME_PIXELS = 256;
const MAX_FRAMES = 64;
const MAX_FRAME_MS = 10_000;
const MAX_LOOPS = 32;
const MAX_ARC_PIXELS = 128;
const MAX_LAUNCH_PIXELS = 128;
const MAX_PATH = 256;
const ID_PATTERN = /^[a-z0-9][a-z0-9_-]{0,63}$/;
const PATH_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._ -]*(\/[A-Za-z0-9][A-Za-z0-9._ -]*)*$/;

/**
 * Reads a value as a plain object.
 *
 * @param value The value.
 * @param where What it is, for the error.
 * @returns The object.
 * @throws Error when it is null, a list or not an object.
 */
function record(value: unknown, where: string): Readonly<Record<string, unknown>> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new Error(`${where} is not an object`);
  }
  return value as Readonly<Record<string, unknown>>;
}

/**
 * Reads a whole number within a range.
 *
 * @param value The value.
 * @param least The least it may be.
 * @param most The most it may be.
 * @param where What it is, for the error.
 * @returns The number.
 * @throws Error when it is not a whole number in the range.
 */
function whole(value: unknown, least: number, most: number, where: string): number {
  if (typeof value !== "number" || !Number.isInteger(value) || value < least || value > most) {
    throw new Error(`${where} is not a whole number from ${least} to ${most}`);
  }
  return value;
}

/**
 * Reads a strip's path: a relative path under the asset base, of plain segments, so it names no other origin and
 * climbs out of no directory.
 *
 * @param value The value.
 * @param where What it is, for the error.
 * @returns The path.
 * @throws Error when it is not such a path.
 */
function stripPath(value: unknown, where: string): string {
  if (typeof value !== "string" || value.length > MAX_PATH || !PATH_PATTERN.test(value) || value.split("/").some((segment) => segment === "." || segment === "..")) {
    throw new Error(`${where} is not a relative path of plain segments`);
  }
  return value;
}

/**
 * Reads one strip.
 *
 * @param value The value.
 * @param where What it is, for the error.
 * @returns The strip.
 * @throws Error naming the field that is not of its kind or outside its range.
 */
function strip(value: unknown, where: string): ProjectileStrip {
  const fields = record(value, where);
  return {
    path: stripPath(fields["path"], `${where}.path`),
    frameWidth: whole(fields["frameWidth"], 1, MAX_FRAME_PIXELS, `${where}.frameWidth`),
    frameHeight: whole(fields["frameHeight"], 1, MAX_FRAME_PIXELS, `${where}.frameHeight`),
    frames: whole(fields["frames"], 1, MAX_FRAMES, `${where}.frames`),
    frameMs: whole(fields["frameMs"], 1, MAX_FRAME_MS, `${where}.frameMs`),
  };
}

/**
 * Reads a projectile look from JSON.
 *
 * @param value The parsed value.
 * @param where What it is, for the error.
 * @returns The look, holding only the fields a look has.
 * @throws Error naming the field that is missing, not of its kind or outside its range.
 */
export function validateProjectileLook(value: unknown, where: string): ProjectileLook {
  const fields = record(value, where);
  const id = fields["id"];
  if (typeof id !== "string" || !ID_PATTERN.test(id)) {
    throw new Error(`${where}.id is not an id of lower case letters, digits, dashes and underscores`);
  }
  const flight = strip(fields["flight"], `${where}.flight`);
  const listed = fields["burst"];
  if (!Array.isArray(listed)) {
    throw new Error(`${where}.burst is not a list`);
  }
  if (listed.length > MAX_BURST_STRIPS) {
    throw new Error(`${where}.burst holds ${listed.length} strips, more than ${MAX_BURST_STRIPS}`);
  }
  const burst = listed.map((entry: unknown, index): ProjectileBurstStrip => {
    const at = `${where}.burst[${index}]`;
    return { ...strip(entry, at), loops: whole(record(entry, at)["loops"], 1, MAX_LOOPS, `${at}.loops`) };
  });
  if (typeof fields["rotates"] !== "boolean") {
    throw new Error(`${where}.rotates is not true or false`);
  }
  const launchPixels = fields["launchPixels"] === undefined ? 0 : whole(fields["launchPixels"], 0, MAX_LAUNCH_PIXELS, `${where}.launchPixels`);
  return { id, flight, burst, rotates: fields["rotates"], arcPixels: whole(fields["arcPixels"], 0, MAX_ARC_PIXELS, `${where}.arcPixels`), launchPixels };
}

/**
 * Reads a table of looks from JSON: `{ looks: { <id>: look } }`, each look filed under its own id.
 *
 * @param value The parsed value.
 * @returns The table.
 * @throws Error naming what is malformed: the table, a look's field, a look filed under another id, or more looks
 *   than `MAX_PROJECTILE_LOOKS`.
 */
export function validateProjectileLooks(value: unknown): ProjectileLookTable {
  const listed = record(record(value, "the projectile looks")["looks"], "looks");
  const ids = Object.keys(listed);
  if (ids.length > MAX_PROJECTILE_LOOKS) {
    throw new Error(`looks holds ${ids.length} looks, more than ${MAX_PROJECTILE_LOOKS}`);
  }
  const looks: Record<string, ProjectileLook> = {};
  for (const id of ids) {
    const look = validateProjectileLook(listed[id], `looks.${id}`);
    if (look.id !== id) {
      throw new Error(`looks.${id}.id is ${look.id}, not the id it is filed under`);
    }
    looks[id] = look;
  }
  return { looks };
}

/**
 * How long a look's burst plays: every strip's frames, by how often it plays through.
 *
 * @param look The look.
 * @returns The length in ms, 0 for a look with no burst.
 */
export function burstLengthMs(look: ProjectileLook): number {
  let length = 0;
  for (let index = 0; index < look.burst.length; index += 1) {
    const part = look.burst[index] as ProjectileBurstStrip;
    length += part.frames * part.frameMs * part.loops;
  }
  return length;
}

/**
 * The strip and frame a burst shows at a time since the landing. Allocates nothing.
 *
 * @param look The look.
 * @param elapsedMs The time since the landing.
 * @param out Filled with the strip's index and the frame when something shows.
 * @returns False before the landing, once the burst has played out, or for a look with no burst.
 */
export function burstFrameAt(look: ProjectileLook, elapsedMs: number, out: BurstFrame): boolean {
  if (!(elapsedMs >= 0)) {
    return false;
  }
  let left = elapsedMs;
  for (let index = 0; index < look.burst.length; index += 1) {
    const part = look.burst[index] as ProjectileBurstStrip;
    const length = part.frames * part.frameMs * part.loops;
    if (left < length) {
      out.strip = index;
      out.frame = Math.floor(left / part.frameMs) % part.frames;
      return true;
    }
    left -= length;
  }
  return false;
}
